// Budget Health Thresholds Storage
// Manages configurable thresholds that drive the project health badge (green/amber/red)
// shown on the Project List, Dashboard, Portfolio, and Overview tab.
//
// Health is a multi-dimensional rollup across 8 dimensions:
//   1. Cost              — total cost consumed vs total cost budget
//   2. Schedule          — completion % vs timeline elapsed %
//   3. Hours             — hours consumed % vs completion % (gap signals over-resourcing)
//   4. WIP               — WIP amount and under-billing gap
//   5. Milestones        — any overdue milestones
//   6. Margin            — actual gross margin % vs configured thresholds
//   7. Realisation Rate  — actual billed vs potential billable value
//   8. Forecast Schedule — forecastEndDate vs endDate (days overrun)
//
// Health = worst single signal across all active dimensions.
// A project is only green if ALL evaluated dimensions are green.
//
// Phase 2: API-backed with module-level cache for synchronous reads.
// Falls back to localStorage/defaults if the API is unavailable.

import { apiFetch } from './api-error-handler';
import { getApiBaseUrl } from './data-service';

export interface BudgetHealthThresholds {
  // ── Dimension 1: Cost ──────────────────────────────────────────────────
  /** % of total cost budget consumed at which health turns amber. Default: 80 */
  costAmberThreshold: number;
  /** % of total cost budget consumed at which health turns red. Default: 110 */
  costRedThreshold: number;

  // ── Dimension 2: Schedule ──────────────────────────────────────────────
  /** % gap (timeElapsed - completion) at which health turns amber. Default: 10 */
  scheduleAmberGap: number;
  /** % gap (timeElapsed - completion) at which health turns red. Default: 20 */
  scheduleRedGap: number;

  // ── Dimension 3: Hours ─────────────────────────────────────────────────
  /** % gap (hoursConsumed% - completionPercent) at which health turns amber. Default: 15 */
  hoursAmberGap: number;
  /** % gap (hoursConsumed% - completionPercent) at which health turns red. Default: 30 */
  hoursRedGap: number;

  // ── Dimension 4: WIP / Billing ─────────────────────────────────────────
  /**
   * Controls how WIP amber/red thresholds are evaluated.
   * 'absolute' — compares raw £ WIP amount against wipAmberThreshold / wipRedThreshold.
   * 'percent'  — compares WIP as a % of budgeted revenue against wipAmberPct / wipRedPct.
   * Default: 'absolute' (preserves existing behaviour for current installations).
   */
  wipThresholdMode: 'absolute' | 'percent';
  /** WIP amount (£) at which health turns amber. Used when wipThresholdMode = 'absolute'. Default: 30000 */
  wipAmberThreshold: number;
  /** WIP amount (£) at which health turns red. Used when wipThresholdMode = 'absolute'. Default: 50000 */
  wipRedThreshold: number;
  /** WIP as % of budgeted revenue at which health turns amber. Used when wipThresholdMode = 'percent'. Default: 15 */
  wipAmberPct: number;
  /** WIP as % of budgeted revenue at which health turns red. Used when wipThresholdMode = 'percent'. Default: 25 */
  wipRedPct: number;
  /** Under-billing % gap at which health turns amber. Default: 5 */
  billingAmberGap: number;
  /** Under-billing % gap at which health turns red. Default: 10 */
  billingRedGap: number;

  // ── Dimension 5: Milestones ────────────────────────────────────────────
  /** Any overdue milestone triggers red. Default: true */
  milestoneRedOnAnyOverdue: boolean;
  /**
   * Number of days ahead to look for upcoming milestones.
   * If any milestone is due within this many days (but not yet overdue), health turns amber.
   * Set to 0 to disable the amber warning. Default: 7
   */
  milestoneAmberDaysWarning: number;

  // ── Dimension 6: Gross Margin ─────────────────────────────────────────
  /** Actual GM% below this value turns amber. Default: 15 */
  marginAmberThreshold: number;
  /** Actual GM% below this value turns red. Default: 10 */
  marginRedThreshold: number;
  /**
   * When false, the margin dimension is excluded from health rollup.
   * Finance can disable this until nominal code mapping is ready.
   * Default: true
   */
  marginEnabled: boolean;

  // ── Dimension 7: Realisation Rate ─────────────────────────────────────
  /** Realisation rate % below this value turns amber. Default: 90 */
  realisationAmberThreshold: number;
  /** Realisation rate % below this value turns red. Default: 75 */
  realisationRedThreshold: number;
  /**
   * When false, the realisation rate dimension is excluded from the health rollup.
   * Can be disabled if the firm does not use billable rate cards.
   * Default: true
   */
  realisationEnabled: boolean;

  // ── Dimension 8: Forecast Schedule ─────────────────────────────────────
  /** Days forecastEndDate exceeds endDate at which health turns amber. Default: 28 */
  forecastScheduleAmberDays: number;
  /** Days forecastEndDate exceeds endDate at which health turns red. Default: 56 */
  forecastScheduleRedDays: number;

  /** Last updated ISO timestamp (stamped automatically on save) */
  lastUpdated?: string;
}

const STORAGE_KEY = 'budget-health-thresholds';

export const DEFAULT_BUDGET_HEALTH_THRESHOLDS: BudgetHealthThresholds = {
  // Cost
  costAmberThreshold: 80,
  costRedThreshold: 110,
  // Schedule
  scheduleAmberGap: 10,
  scheduleRedGap: 20,
  // Hours
  hoursAmberGap: 15,
  hoursRedGap: 30,
  // WIP / Billing
  wipThresholdMode: 'absolute',
  wipAmberThreshold: 30000,
  wipRedThreshold: 50000,
  wipAmberPct: 15,
  wipRedPct: 25,
  billingAmberGap: 5,
  billingRedGap: 10,
  // Milestones
  milestoneRedOnAnyOverdue: true,
  milestoneAmberDaysWarning: 7,
  // Margin
  marginAmberThreshold: 15,
  marginRedThreshold: 10,
  marginEnabled: true,
  // Realisation Rate
  realisationAmberThreshold: 90,
  realisationRedThreshold: 75,
  realisationEnabled: true,
  // Forecast Schedule
  forecastScheduleAmberDays: 28,
  forecastScheduleRedDays: 56,
};

// ── Module-level cache ──────────────────────────────────────────────────────
let _cache: BudgetHealthThresholds = { ...DEFAULT_BUDGET_HEALTH_THRESHOLDS };
let _hasFetched = false;

/**
 * Synchronous read — returns the cached budget health thresholds.
 * Always returns a valid object (defaults if the API hasn't been called yet).
 * Falls back to localStorage for legacy data on first access.
 */
export function getBudgetHealthThresholds(): BudgetHealthThresholds {
  if (!_hasFetched) {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, unknown>;
        if (typeof parsed.amberThreshold === 'number' && parsed.costAmberThreshold === undefined) {
          parsed.costAmberThreshold = parsed.amberThreshold;
        }
        if (typeof parsed.redThreshold === 'number' && parsed.costRedThreshold === undefined) {
          parsed.costRedThreshold = parsed.redThreshold;
        }
        _cache = { ...DEFAULT_BUDGET_HEALTH_THRESHOLDS, ...parsed };
      }
    } catch (error) {
      console.error('Error loading budget health thresholds from localStorage:', error);
    }
  }
  return _cache;
}

/**
 * Fetch budget health thresholds from the server and update the cache.
 * Should be called during app bootstrap and after saving.
 */
export async function fetchBudgetHealthThresholds(): Promise<BudgetHealthThresholds> {
  try {
    const baseUrl = getApiBaseUrl();
    const data = await apiFetch<BudgetHealthThresholds>(
      `${baseUrl}/settings/project/health-thresholds`
    );
    _cache = { ...DEFAULT_BUDGET_HEALTH_THRESHOLDS, ...data };
    _hasFetched = true;
    // Keep localStorage in sync as a fallback
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_cache)); } catch { /* ignore */ }
  } catch (error: unknown) {
    // 401/403 during bootstrap is expected when the org context is not
    // yet established — log as a warning to reduce console noise.
    const category = (error as Record<string, unknown>)?.category;
    if (category === 'authentication' || category === 'authorization') {
      console.warn('Budget health thresholds API returned auth error, using defaults');
    } else {
      console.error('Error fetching budget health thresholds from API:', error);
    }
    if (!_hasFetched) {
      _cache = getBudgetHealthThresholds();
    }
  }
  return _cache;
}

/**
 * Save budget health thresholds to the server and update the cache.
 */
export async function saveBudgetHealthThresholds(thresholds: BudgetHealthThresholds): Promise<void> {
  const baseUrl = getApiBaseUrl();
  const data = await apiFetch<BudgetHealthThresholds>(
    `${baseUrl}/settings/project/health-thresholds`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(thresholds),
    }
  );
  _cache = { ...DEFAULT_BUDGET_HEALTH_THRESHOLDS, ...data };
  _hasFetched = true;
  // Keep localStorage in sync as a fallback
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_cache)); } catch { /* ignore */ }
}

// ─────────────────────────────────────────────────────────────────────────────
// Health derivation params
// ─────────────────────────────────────────────────────────────────────────────

export interface DeriveProjectHealthParams {
  // Dimension 1 — Cost
  /** Total actual cost to date (labour + expenses + all other cost categories available) */
  actualCost: number;
  /** Total budget cost (labour + expenses + all other cost categories available) */
  budgetCost: number;

  // Dimension 2 — Schedule
  /** Project start date ISO string. If null/undefined, schedule dimension is skipped. */
  startDate?: string | null;
  /** Project end date ISO string. If null/undefined, schedule dimension is skipped. */
  endDate?: string | null;
  /** Effective completion % (override wins over system value). Range: 0–100. */
  completionPercent: number;

  // Dimension 3 — Hours
  /** Actual hours spent to date */
  actualHours: number;
  /** Total budgeted hours. If 0, hours dimension is skipped. */
  budgetHours: number;

  // Dimension 4 — WIP / Billing
  /** Current WIP amount (£) */
  wipAmount: number;
  /** Actual revenue invoiced to date */
  actualRevenue: number;
  /** Total revenue budget. If 0, billing gap dimension is skipped. */
  budgetRevenue: number;

  // Dimension 5 — Milestones
  /** Number of overdue milestones */
  overdueMilestoneCount: number;
  /**
   * Number of pending milestones whose due date falls within the amber warning window
   * (i.e. due in the next N days but not yet past due).
   * If undefined or null, the amber tier is skipped (e.g. at list level where milestones are not loaded).
   */
  upcomingMilestoneCount?: number | null;

  // Dimension 6 — Gross Margin
  /**
   * Actual gross margin % (0–100 scale, can be negative).
   * If undefined or null, margin dimension is skipped regardless of marginEnabled.
   */
  actualGrossMarginPct?: number | null;

  // Dimension 7 — Realisation Rate
  /**
   * Realisation rate % (0–100+ scale): (actualBilled / potentialBillableValue) × 100.
   * Calculated as: SUM(approved billable hours × billRate) vs actual revenue billed.
   * If undefined or null, realisation dimension is skipped regardless of realisationEnabled.
   * Only available in project detail context where timesheet entries are loaded.
   */
  realisationRatePct?: number | null;

  // Dimension 8 — Forecast Schedule
  /**
   * PM-entered forecast completion date (ISO string).
   * Compared against endDate — if forecastEndDate > endDate by more than the
   * configured amber/red day thresholds, a schedule-forecast signal is raised.
   * If null/undefined or endDate is null, this dimension is skipped.
   */
  forecastEndDate?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pure health derivation function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pure function: derive a project health status from multi-dimensional signals
 * and the configured thresholds.
 *
 * Returns the WORST signal across all evaluated dimensions:
 *   green  — all dimensions healthy
 *   amber  — at least one dimension amber, none red
 *   red    — at least one dimension red
 *
 * Dimensions are skipped gracefully when data is unavailable (e.g. no end date,
 * zero budget hours, zero revenue budget).
 */
export function deriveProjectHealth(
  params: DeriveProjectHealthParams,
  thresholds: BudgetHealthThresholds
): 'green' | 'amber' | 'red' {
  const signals: ('green' | 'amber' | 'red')[] = [];

  // ── Dimension 1: Cost ────────────────────────────────────────────────────
  if (params.budgetCost > 0) {
    const costPercent = (params.actualCost / params.budgetCost) * 100;
    if (costPercent >= thresholds.costRedThreshold) {
      signals.push('red');
    } else if (costPercent >= thresholds.costAmberThreshold) {
      signals.push('amber');
    } else {
      signals.push('green');
    }
  }

  // ── Dimension 2: Schedule ────────────────────────────────────────────────
  if (params.startDate && params.endDate) {
    const start = new Date(params.startDate).getTime();
    const end = new Date(params.endDate).getTime();
    const now = Date.now();
    const totalDuration = end - start;

    if (totalDuration > 0) {
      const timeElapsedPercent = Math.min(((now - start) / totalDuration) * 100, 100);
      const scheduleGap = timeElapsedPercent - params.completionPercent;

      if (scheduleGap >= thresholds.scheduleRedGap) {
        signals.push('red');
      } else if (scheduleGap >= thresholds.scheduleAmberGap) {
        signals.push('amber');
      } else {
        signals.push('green');
      }
    }
  }

  // ── Dimension 3: Hours ───────────────────────────────────────────────────
  if (params.budgetHours > 0) {
    const hoursConsumedPercent = (params.actualHours / params.budgetHours) * 100;
    const hoursGap = hoursConsumedPercent - params.completionPercent;

    if (hoursGap >= thresholds.hoursRedGap) {
      signals.push('red');
    } else if (hoursGap >= thresholds.hoursAmberGap) {
      signals.push('amber');
    } else {
      signals.push('green');
    }
  }

  // ── Dimension 4a: WIP Amount ─────────────────────────────────────────────
  // Mode: 'absolute' — compare raw £ WIP against fixed thresholds (default, backward-compatible)
  // Mode: 'percent'  — compare WIP as % of budgeted revenue (skipped if budgetRevenue is 0)
  if (thresholds.wipThresholdMode === 'percent') {
    if (params.budgetRevenue > 0) {
      const wipPct = (params.wipAmount / params.budgetRevenue) * 100;
      if (wipPct >= thresholds.wipRedPct) {
        signals.push('red');
      } else if (wipPct >= thresholds.wipAmberPct) {
        signals.push('amber');
      } else {
        signals.push('green');
      }
    }
    // If budgetRevenue is 0 in percent mode, skip this dimension gracefully
  } else {
    // 'absolute' mode (default)
    if (params.wipAmount >= thresholds.wipRedThreshold) {
      signals.push('red');
    } else if (params.wipAmount >= thresholds.wipAmberThreshold) {
      signals.push('amber');
    } else {
      signals.push('green');
    }
  }

  // ── Dimension 4b: Billing Gap ────────────────────────────────────────────
  if (params.budgetRevenue > 0) {
    const billedPercent = (params.actualRevenue / params.budgetRevenue) * 100;
    const billingGap = params.completionPercent - billedPercent; // positive = under-billing

    if (billingGap >= thresholds.billingRedGap) {
      signals.push('red');
    } else if (billingGap >= thresholds.billingAmberGap) {
      signals.push('amber');
    } else {
      signals.push('green');
    }
  }

  // ── Dimension 5: Milestones ──────────────────────────────────────────────
  // Red: any milestone is already past its due date (and milestoneRedOnAnyOverdue is on)
  // Amber: any pending milestone is due within the warning window (milestoneAmberDaysWarning days)
  // Green: no overdue or imminent milestones
  // Skipped entirely when milestone data is not loaded (overdueMilestoneCount === 0 and upcomingMilestoneCount is null/undefined at list level)
  if (thresholds.milestoneRedOnAnyOverdue && params.overdueMilestoneCount > 0) {
    signals.push('red');
  } else if (
    thresholds.milestoneAmberDaysWarning > 0 &&
    params.upcomingMilestoneCount != null &&
    params.upcomingMilestoneCount > 0
  ) {
    signals.push('amber');
  } else if (params.upcomingMilestoneCount != null || params.overdueMilestoneCount > 0) {
    // Only push green when we actually have milestone data to evaluate
    signals.push('green');
  }

  // ── Dimension 6: Gross Margin ───────────────────────────────────────────
  // Only evaluated when marginEnabled is true AND actualGrossMarginPct is available.
  if (thresholds.marginEnabled && params.actualGrossMarginPct != null) {
    const gm = params.actualGrossMarginPct;
    if (gm < thresholds.marginRedThreshold) {
      signals.push('red');
    } else if (gm < thresholds.marginAmberThreshold) {
      signals.push('amber');
    } else {
      signals.push('green');
    }
  }

  // ── Dimension 7: Realisation Rate ───────────────────────────────────────
  // Only evaluated when realisationEnabled is true AND realisationRatePct is available.
  // Not available at the project list level (requires loaded timesheet entries).
  if (thresholds.realisationEnabled && params.realisationRatePct != null) {
    const rate = params.realisationRatePct;
    if (rate < thresholds.realisationRedThreshold) {
      signals.push('red');
    } else if (rate < thresholds.realisationAmberThreshold) {
      signals.push('amber');
    } else {
      signals.push('green');
    }
  }

  // ── Dimension 8: Forecast Schedule ───────────────────────────────────
  // Compares forecastEndDate vs endDate. If forecast exceeds end by more than
  // the configured thresholds (days), raise amber/red.
  if (params.forecastEndDate && params.endDate) {
    const forecast = new Date(params.forecastEndDate).getTime();
    const end = new Date(params.endDate).getTime();
    const diffDays = (forecast - end) / (1000 * 60 * 60 * 24);

    if (diffDays >= thresholds.forecastScheduleRedDays) {
      signals.push('red');
    } else if (diffDays >= thresholds.forecastScheduleAmberDays) {
      signals.push('amber');
    } else {
      signals.push('green');
    }
  }

  // ── Rollup: return worst signal ──────────────────────────────────────────
  if (signals.includes('red')) return 'red';
  if (signals.includes('amber')) return 'amber';
  return 'green';
}

// ─────────────────────────────────────────────────────────────────────────────
// Health breakdown types & function
// ─────────────────────────────────────────────────────────────────────────────

export interface HealthDimensionResult {
  /** Display label for this dimension */
  label: string;
  /** The signal for this dimension */
  signal: 'green' | 'amber' | 'red';
  /** Short explanation of why this signal was reached */
  detail: string;
}

export interface ProjectHealthBreakdown {
  /** Overall rollup signal (worst across all evaluated dimensions) */
  health: 'green' | 'amber' | 'red';
  /** Per-dimension results — only includes dimensions that were evaluated (not skipped) */
  dimensions: HealthDimensionResult[];
}

/**
 * Like `deriveProjectHealth` but also returns a per-dimension breakdown.
 * Use this when rendering the health badge tooltip.
 */
export function deriveProjectHealthWithBreakdown(
  params: DeriveProjectHealthParams,
  thresholds: BudgetHealthThresholds
): ProjectHealthBreakdown {
  const dimensions: HealthDimensionResult[] = [];

  // ── Dimension 1: Cost ────────────────────────────────────────────────────
  if (params.budgetCost > 0) {
    const costPercent = (params.actualCost / params.budgetCost) * 100;
    const formatted = `${costPercent.toFixed(0)}% of budget used`;
    if (costPercent >= thresholds.costRedThreshold) {
      dimensions.push({ label: 'Cost', signal: 'red', detail: formatted });
    } else if (costPercent >= thresholds.costAmberThreshold) {
      dimensions.push({ label: 'Cost', signal: 'amber', detail: formatted });
    } else {
      dimensions.push({ label: 'Cost', signal: 'green', detail: formatted });
    }
  }

  // ── Dimension 2: Schedule ────────────────────────────────────────────────
  if (params.startDate && params.endDate) {
    const start = new Date(params.startDate).getTime();
    const end = new Date(params.endDate).getTime();
    const now = Date.now();
    const totalDuration = end - start;
    if (totalDuration > 0) {
      const timeElapsedPercent = Math.min(((now - start) / totalDuration) * 100, 100);
      const scheduleGap = timeElapsedPercent - params.completionPercent;
      const formatted = scheduleGap > 0
        ? `${scheduleGap.toFixed(0)}% behind schedule`
        : `On track (${Math.abs(scheduleGap).toFixed(0)}% ahead)`;
      if (scheduleGap >= thresholds.scheduleRedGap) {
        dimensions.push({ label: 'Schedule', signal: 'red', detail: formatted });
      } else if (scheduleGap >= thresholds.scheduleAmberGap) {
        dimensions.push({ label: 'Schedule', signal: 'amber', detail: formatted });
      } else {
        dimensions.push({ label: 'Schedule', signal: 'green', detail: formatted });
      }
    }
  }

  // ── Dimension 3: Hours ───────────────────────────────────────────────────
  if (params.budgetHours > 0) {
    const hoursConsumedPercent = (params.actualHours / params.budgetHours) * 100;
    const hoursGap = hoursConsumedPercent - params.completionPercent;
    const formatted = hoursGap > 0
      ? `${hoursGap.toFixed(0)}% over hours pace`
      : `On track`;
    if (hoursGap >= thresholds.hoursRedGap) {
      dimensions.push({ label: 'Hours', signal: 'red', detail: formatted });
    } else if (hoursGap >= thresholds.hoursAmberGap) {
      dimensions.push({ label: 'Hours', signal: 'amber', detail: formatted });
    } else {
      dimensions.push({ label: 'Hours', signal: 'green', detail: formatted });
    }
  }

  // ── Dimension 4a: WIP Amount ─────────────────────────────────────────────
  if (thresholds.wipThresholdMode === 'percent') {
    if (params.budgetRevenue > 0) {
      const wipPct = (params.wipAmount / params.budgetRevenue) * 100;
      const formatted = `${wipPct.toFixed(0)}% of revenue budget`;
      if (wipPct >= thresholds.wipRedPct) {
        dimensions.push({ label: 'WIP', signal: 'red', detail: formatted });
      } else if (wipPct >= thresholds.wipAmberPct) {
        dimensions.push({ label: 'WIP', signal: 'amber', detail: formatted });
      } else {
        dimensions.push({ label: 'WIP', signal: 'green', detail: formatted });
      }
    }
  } else {
    const formatted = `£${params.wipAmount.toLocaleString()} outstanding`;
    if (params.wipAmount >= thresholds.wipRedThreshold) {
      dimensions.push({ label: 'WIP', signal: 'red', detail: formatted });
    } else if (params.wipAmount >= thresholds.wipAmberThreshold) {
      dimensions.push({ label: 'WIP', signal: 'amber', detail: formatted });
    } else {
      dimensions.push({ label: 'WIP', signal: 'green', detail: formatted });
    }
  }

  // ── Dimension 4b: Billing Gap ────────────────────────────────────────────
  if (params.budgetRevenue > 0) {
    const billedPercent = (params.actualRevenue / params.budgetRevenue) * 100;
    const billingGap = params.completionPercent - billedPercent;
    const formatted = billingGap > 0
      ? `${billingGap.toFixed(0)}% under-billed vs completion`
      : `Billing on track`;
    if (billingGap >= thresholds.billingRedGap) {
      dimensions.push({ label: 'Billing Gap', signal: 'red', detail: formatted });
    } else if (billingGap >= thresholds.billingAmberGap) {
      dimensions.push({ label: 'Billing Gap', signal: 'amber', detail: formatted });
    } else {
      dimensions.push({ label: 'Billing Gap', signal: 'green', detail: formatted });
    }
  }

  // ── Dimension 5: Milestones ──────────────────────────────────────────────
  if (thresholds.milestoneRedOnAnyOverdue && params.overdueMilestoneCount > 0) {
    dimensions.push({
      label: 'Milestones',
      signal: 'red',
      detail: `${params.overdueMilestoneCount} overdue`,
    });
  } else if (
    thresholds.milestoneAmberDaysWarning > 0 &&
    params.upcomingMilestoneCount != null &&
    params.upcomingMilestoneCount > 0
  ) {
    dimensions.push({
      label: 'Milestones',
      signal: 'amber',
      detail: `${params.upcomingMilestoneCount} due within ${thresholds.milestoneAmberDaysWarning}d`,
    });
  } else if (params.upcomingMilestoneCount != null || params.overdueMilestoneCount > 0) {
    dimensions.push({ label: 'Milestones', signal: 'green', detail: 'No overdue or upcoming' });
  }

  // ── Dimension 6: Gross Margin ────────────────────────────────────────────
  if (thresholds.marginEnabled && params.actualGrossMarginPct != null) {
    const gm = params.actualGrossMarginPct;
    const formatted = `${gm.toFixed(1)}% gross margin`;
    if (gm < thresholds.marginRedThreshold) {
      dimensions.push({ label: 'Gross Margin', signal: 'red', detail: formatted });
    } else if (gm < thresholds.marginAmberThreshold) {
      dimensions.push({ label: 'Gross Margin', signal: 'amber', detail: formatted });
    } else {
      dimensions.push({ label: 'Gross Margin', signal: 'green', detail: formatted });
    }
  }

  // ── Dimension 7: Realisation Rate ───────────────────────────────────────
  if (thresholds.realisationEnabled && params.realisationRatePct != null) {
    const rate = params.realisationRatePct;
    const formatted = `${rate.toFixed(0)}% realisation`;
    if (rate < thresholds.realisationRedThreshold) {
      dimensions.push({ label: 'Realisation Rate', signal: 'red', detail: formatted });
    } else if (rate < thresholds.realisationAmberThreshold) {
      dimensions.push({ label: 'Realisation Rate', signal: 'amber', detail: formatted });
    } else {
      dimensions.push({ label: 'Realisation Rate', signal: 'green', detail: formatted });
    }
  }

  // ── Dimension 8: Forecast Schedule ─────────────────────────────────────
  if (params.forecastEndDate && params.endDate) {
    const forecast = new Date(params.forecastEndDate).getTime();
    const end = new Date(params.endDate).getTime();
    const diffDays = Math.round((forecast - end) / (1000 * 60 * 60 * 24));
    const formatted = diffDays > 0
      ? `Forecast ${diffDays}d past end date`
      : `Forecast on or before end date`;
    if (diffDays >= thresholds.forecastScheduleRedDays) {
      dimensions.push({ label: 'Forecast Schedule', signal: 'red', detail: formatted });
    } else if (diffDays >= thresholds.forecastScheduleAmberDays) {
      dimensions.push({ label: 'Forecast Schedule', signal: 'amber', detail: formatted });
    } else {
      dimensions.push({ label: 'Forecast Schedule', signal: 'green', detail: formatted });
    }
  }

  // ── Rollup ───────────────────────────────────────────────────────────────
  const signals = dimensions.map((d) => d.signal);
  const health: 'green' | 'amber' | 'red' = signals.includes('red')
    ? 'red'
    : signals.includes('amber')
    ? 'amber'
    : 'green';

  return { health, dimensions };
}
